import { ViteReactSSG } from 'vite-react-ssg';
import { routes } from './routes';
import { setLanguageFromPath, loadLanguage } from './config/i18n';
import { installGlobalErrorHandlers } from './lib/errorReporter';
import { initGA, grantAnalyticsConsent } from './lib/analytics';
import { initPixel, grantPixelConsent } from './lib/metaPixel';
import { hasConsentedToCookies } from './components/CookieConsent';
import './index.css';
import 'flag-icons/css/flag-icons.min.css';

// Install global error/rejection handlers (client-side only)
if (typeof window !== 'undefined') {
  installGlobalErrorHandlers();

  // Chunk-load recovery. A lazy-loaded route module can fail to fetch when:
  //  - the user's session predates the latest deploy (old index.html still
  //    references chunk hashes that no longer exist on the server)
  //  - the user's network drops mid-request (common on mobile)
  //  - a CDN edge briefly 404s a fresh asset
  // Without recovery the user sees a blank screen or "Failed to fetch
  // dynamically imported module" toast. The fix: on first such failure,
  // hard-reload — the new index.html points at current chunks and the
  // navigation retries. Guarded by a session flag so we don't loop if the
  // failure is real (chunk genuinely missing). Reported pattern:
  // err_mpevvnn7_c1ftdt (designer chunk, mobile, 2026-05-21).
  const handleChunkError = (err: unknown) => {
    const msg = String((err as { message?: string })?.message || err);
    if (!/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Unable to preload CSS/i.test(msg)) {
      return false;
    }
    if (sessionStorage.getItem('chunk-error-reload') === '1') {
      return false; // already tried once this session; don't loop
    }
    sessionStorage.setItem('chunk-error-reload', '1');
    window.location.reload();
    return true;
  };
  // Vite fires this for <link rel="modulepreload"> failures.
  window.addEventListener('vite:preloadError', (e) => {
    if (handleChunkError((e as CustomEvent).detail || e)) e.preventDefault();
  });
  // Runtime import() rejections that escape route Suspense boundaries.
  window.addEventListener('unhandledrejection', (e) => {
    if (handleChunkError(e.reason)) e.preventDefault();
  });

  // A chunk whose body is HTML (a 200 interstitial/error page cached under a
  // JS URL by a stale service worker — see public/sw.js) parses as a module
  // and throws SyntaxError "Unexpected token '<'". That surfaces as a window
  // 'error', not a rejection, so handleChunkError above never sees it, and a
  // plain reload would re-serve the poisoned chunk. Purge the SW caches and
  // unregister, then reload once, so the browser refetches fresh chunks.
  window.addEventListener('error', (e) => {
    const msg = String((e as ErrorEvent).message || '');
    if (!/Unexpected token '?<'?/.test(msg)) return;
    if (sessionStorage.getItem('chunk-error-reload') === '1') return;
    sessionStorage.setItem('chunk-error-reload', '1');
    void (async () => {
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(
            keys.filter((k) => k.startsWith('cctvplanner-')).map((k) => caches.delete(k)),
          );
        }
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
      } catch {
        /* best effort — reload anyway */
      }
      window.location.reload();
    })();
  });

  // GA4 + Meta Pixel init. Both default to consent-denied so the loaders run
  // (allowing instant fire after grant) but no cookies/IDs are set until the
  // user accepts the cookie banner. PageView fires from <RouteTracker> on
  // every route change for both providers.
  initGA();
  initPixel();
  // Restore granted consent from a prior session (localStorage).
  if (hasConsentedToCookies()) {
    grantAnalyticsConsent();
    grantPixelConsent();
  }

  // Register service worker for static asset caching (production only).
  // Vendor chunks (cameras-db 1.4MB, vendor-three 996KB, vendor-pdf 889KB)
  // become instant on second visit. SW versioning is in /sw.js — bumped per release.
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Silent fail — SW is progressive enhancement, not critical.
      });
    });

    // When a new SW with skipWaiting + clients.claim takes over mid-session,
    // the active page is still wired to the old asset URLs. Without a hard
    // reload we get a vendor-chunk mismatch the moment the user triggers any
    // dynamic import (Stripe Checkout, PDF export, three.js V3 designer).
    // First-time SW registration also fires this event — use the
    // `hasController` flag to skip that case so the very first visit doesn't
    // self-reload.
    let hasController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hasController) {
        window.location.reload();
      } else {
        hasController = true;
      }
    });
  }
}

// Extract language from a path (same logic as setLanguageFromPath but returns the lang)
function extractLang(pathname: string): string {
  const LANG_PREFIXES = ['pl', 'de', 'es', 'fil', 'hi', 'id', 'pt-br', 'pt-pt', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt', 'ro', 'sv'];
  const segments = pathname.split('/').filter(Boolean);
  return (segments[0] && LANG_PREFIXES.includes(segments[0])) ? segments[0] : 'en';
}

export const createRoot = ViteReactSSG(
  { routes },
  async (context) => {
    // Determine which language this page needs
    const pathname = context.routePath || (context.isClient ? window.location.pathname : '/');
    const lang = extractLang(pathname);

    // Load language resources (async, works in both SSG/Node and client)
    // English is always available; non-English languages are loaded on demand.
    if (lang !== 'en') {
      await loadLanguage(lang);
    }

    // Set the language synchronously on the i18n instance (resources are now available)
    setLanguageFromPath(pathname);
  },
);
